import Controller from './controller'
import { uniqueId } from '../utilities'

export default class extends Controller {
  static get targets() {
    return ['inner', 'content']
  }

  static get options() {
    return {
      speed: 50,
    }
  }

  initialize() {
    this.resize = this.resize.bind(this)
    this.resizeDebounced = this.resizeDebounced.bind(this)
    this.checkPageVisibility = this.checkPageVisibility.bind(this)

    this.resize()
    this.createAnimation()
    this.checkPageVisibility()
    this.playWhenInView()
    this.element.classList.add('is-shown')

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.height) {
          this.resizeDebounced()
        }
      }
    })
    resizeObserver.observe(this.element)

    document.addEventListener('visibilitychange', this.checkPageVisibility)
  }

  setPlayState() {
    if (this.isPageVisible && this.isInView && !this.isHovered) {
      this.play()
    } else {
      this.pause()
    }
  }

  play() {
    if (this.isPlaying) return
    this.isPlaying = true

    this.element.classList.add('is-playing')
    this.innerTarget.style.animationPlayState = 'running'
  }

  pause() {
    if (!this.isPlaying) return
    this.isPlaying = false

    this.element.classList.remove('is-playing')
    this.innerTarget.style.animationPlayState = 'paused'
  }

  playWhenInView() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.isInView = true
        } else {
          this.isInView = false
        }

        this.setPlayState()
      })
    })

    observer.observe(this.element)
  }

  createAnimation() {
    const animationName = uniqueId('_animation_')
    const style = document.createElement('style')

    style.textContent = `
      @keyframes ${animationName} {
        to {
          transform: translateX(calc(var(--marquee-width) * -1));
        }
      }
    `

    document.head.appendChild(style)
    this.innerTarget.style.animationName = animationName
  }

  resize() {
    // Check how many clones of `this.contentTarget` are needed to fill the marquee
    const width = this.contentTarget.getBoundingClientRect().width
    if (width === 0) return

    const count = Math.ceil(window.innerWidth / width)

    // Adjust speed by adding e.g. data-marquee-options='{"speed": 50}' on data-controller="marquee" element
    this.innerTarget.style.animationDuration = `${width / this.options.speed}s`
    this.element.style.setProperty('--marquee-width', `${width}px`)

    // If the number of clones needed has changed, clear the previous clones and add the new amount
    if (count !== this.prevCount) {
      if (this.extraContent) {
        for (const content of this.extraContent) {
          this.innerTarget.removeChild(content)
        }
      }

      this.extraContent = Array.from(Array(count)).map(() => {
        const content = this.contentTarget.cloneNode(true)
        content.removeAttribute('data-marquee-target')

        Array.from(content.querySelectorAll('[data-visible-target="item"]')).forEach((element) => {
          element.removeAttribute('data-visible-target')
          element.classList.remove('animate-up', 'animate-fade', 'animate-down')
        })

        this.innerTarget.appendChild(content)
        return content
      })

      this.prevCount = count
    }
    
    var items = this.innerTarget.getElementsByClassName('marquee__item')
    for (let index = 0; index < items.length; index++) {
      const element = items[index];

      if (index % 2) {
        element.classList.add('is-even')
      } else {
        element.classList.remove('is-even')
      }
    }
  }

  resizeDebounced() {
    clearTimeout(this.resizeDebouncedTimeout)
    this.resizeDebouncedTimeout = setTimeout(this.resize, 150)
  }

  checkPageVisibility() {
    this.isPageVisible = document.visibilityState !== 'hidden'
    this.setPlayState()
  }

  hover() {
    this.isHovered = true
    this.setPlayState()
  }

  unhover() {
    this.isHovered = false
    this.setPlayState()
  }
}
